import {useDispatch} from 'react-redux';

import {ContextTarget, documentContextApi, useCreateDocumentContextMutation} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {ApiError, isDefined, Nullish} from 'shared/web';

export interface FileFieldValue {
  fileId: string;
  fileName: string;
  fileUri: string;
  fileType?: string;
}

export interface FileFieldConfig {
  fieldValue: FileFieldValue | Nullish;
  documentNote: string;
}

export interface SyncDocumentsParams {
  vehicleId: string;
  contextTarget: ContextTarget;
  fileFields: FileFieldConfig[];
}

export interface SyncResult {
  success: boolean;
  documentContextId?: string;
  error?: unknown;
}

/**
 * Hook to sync uploaded files with document-context system
 * Used in form submit handlers after vehicle is created/updated
 *
 * @example
 * const {syncDocuments} = useVehicleDocumentSync();
 *
 * const handleSubmit = async (values) => {
 *   const vehicle = await createVehicle(values).unwrap();
 *
 *   await syncDocuments({
 *     vehicleId: vehicle.id,
 *     contextTarget: 'vehicle',
 *     fileFields: [
 *       {
 *         fieldValue: values.batteryCertificateDocument,
 *         documentNote: 'Battery Certificate',
 *       },
 *     ],
 *   });
 * };
 */
export function useVehicleDocumentSync() {
  const [createDocumentContext] = useCreateDocumentContextMutation();
  const dispatch = useDispatch();

  const syncDocuments = async (params: SyncDocumentsParams): Promise<SyncResult[]> => {
    // Filter out null values (no file uploaded)
    const filesToSync = params.fileFields.filter(
      (field): field is FileFieldConfig & {fieldValue: FileFieldValue} =>
        isDefined(field.fieldValue)
    );

    // If no files to sync, return early
    if (filesToSync.length === 0) {
      return [];
    }

    // Create document-contexts for each file
    const results = await Promise.all(
      filesToSync.map((field) =>
        createDocumentContext({
          documentContextRequestBody: {
            fileRemoteId: field.fieldValue.fileId,
            contextTarget: params.contextTarget,
            contextId: params.vehicleId,
            fileName: field.fieldValue.fileName,
            note: field.documentNote,
          },
        })
          .unwrap()
          .then((response) => ({
            success: true as const,
            documentContextId: response.id,
          }))
          .then((response) => {
            dispatch(
              documentContextApi.util.invalidateTags([
                {type: 'documentsCount', id: params.vehicleId},
              ])
            );
            return response;
          })
          .catch((error) => {
            handleApiError(error as ApiError);
            return {
              success: false as const,
              error,
            };
          })
      )
    );

    return results;
  };

  return {syncDocuments};
}
